import * as THREE from 'three'
import type { SogsData } from '@/plugins/gsplat/core/SogsData'
import type { SogsMetaV2 } from '@/plugins/gsplat/loaders/SogsParser'
// 导入 shader
import gsplatSogsReorderMRTFrag from '@/plugins/gsplat/shaders/frag/gsplatSogsReorderMRT.fs'
import { registerGSplatChunks } from '@/plugins/gsplat/shaders/gsplat-chunks'
import { getTextureImage } from '@/plugins/gsplat/utils/textureImage'
import { getLoggerManager } from '@/shared/logging'

/** GPU 打包 logger（命名空间 gsplat:gpu），perf 埋点经 log.perf 输出 */
const log = getLoggerManager().getLogger('gsplat:gpu')

/**
 * GPU 打包后的纹理结构
 */
export interface PackedTexturesGPU {
  /** 打包数据纹理 (RGBA32UI) */
  packedTexture: THREE.Texture
  /** 打包颜色纹理 (RGBA8) */
  packedSh0: THREE.Texture
  /** 纹理宽度 */
  width: number
  /** 纹理高度 */
  height: number
}

/**
 * 从 GPU 纹理打包的输入参数
 */
export interface TexturePackParams {
  /** 位置低位纹理 */
  means_l: THREE.Texture
  /** 位置高位纹理 */
  means_u: THREE.Texture
  /** 四元数纹理 */
  quats: THREE.Texture
  /** 缩放纹理 */
  scales: THREE.Texture
  /** SH0 颜色纹理 */
  sh0: THREE.Texture
  /** SH labels 纹理（可选） */
  sh_labels?: THREE.Texture | undefined
  /** splat 数量 */
  numSplats: number
  /** 元数据 */
  meta: SogsMetaV2
}

/**
 * 全屏四边形顶点着色器
 */
const fullscreenVS = /* glsl */ `
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`

/**
 * SOGS GPU 打包器
 *
 * 使用 MRT (Multiple Render Targets) 在 GPU 上执行 SOGS 数据打包
 * 参考 PlayCanvas gsplat-sogs-data.js 的 packGpuMemory() 实现
 *
 * 输出格式:
 * - packedTexture (RGBA32UI): 打包的位置、四元数、缩放、SH labels
 * - packedSh0 (RGBA8): 打包的 SH0 颜色
 */
export class SogsGpuPackerGPU {
  private renderer: THREE.WebGLRenderer
  private fullscreenQuad: THREE.Mesh
  private quadScene: THREE.Scene
  private quadCamera: THREE.OrthographicCamera
  private processedReorderFragment: string

  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer
    this.processedReorderFragment = registerGSplatChunks().assembleSource(gsplatSogsReorderMRTFrag)

    // 创建全屏四边形
    const geometry = new THREE.BufferGeometry()
    const vertices = new Float32Array([-1, -1, 3, -1, -1, 3])
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 2))
    // 预设包围盒/球，避免 computeBoundingSphere 触发 NaN 警告
    geometry.boundingBox = new THREE.Box3(new THREE.Vector3(-1, -1, 0), new THREE.Vector3(3, 3, 0))
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(1, 1, 0), Math.SQRT2 * 2)
    geometry.computeBoundingSphere = () => {}

    this.fullscreenQuad = new THREE.Mesh(geometry)
    this.quadScene = new THREE.Scene()
    this.quadScene.add(this.fullscreenQuad)
    this.quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
  }

  /**
   * 执行 GPU 打包
   *
   * @param sogsData SOGS 压缩数据
   * @returns 打包后的 GPU 纹理
   */
  packGpuMemory(sogsData: SogsData): PackedTexturesGPU {
    const endPack = log.perf.time('gpuPack')
    const meta = sogsData.meta as SogsMetaV2

    // 检查数据是否可用
    if (
      !sogsData.meansLow ||
      !sogsData.meansHigh ||
      !sogsData.quats ||
      !sogsData.scales ||
      !sogsData.sh0
    ) {
      throw new Error('[SogsGpuPackerGPU] SOGS 数据不可用')
    }

    // 使用与 SogsData 相同的纹理尺寸（避免 texSubImage2D 错误）
    const width = sogsData.textureWidth
    const height = sogsData.textureHeight

    // 创建源纹理
    const meansLTex = this.createSourceTexture(sogsData.meansLow, width, height, 'means_l')
    const meansUTex = this.createSourceTexture(sogsData.meansHigh, width, height, 'means_u')
    const quatsTex = this.createSourceTexture(sogsData.quats, width, height, 'quats')
    const scalesTex = this.createSourceTexture(sogsData.scales, width, height, 'scales')
    const sh0Tex = this.createSourceTexture(sogsData.sh0, width, height, 'sh0')

    // SH labels 可能为空
    const shLabelsTex = sogsData.shLabels
      ? this.createSourceTexture(sogsData.shLabels, width, height, 'sh_labels')
      : meansLTex // 使用 dummy 纹理

    // 准备 uniforms
    const uniforms: Record<string, THREE.IUniform> = {
      means_l: { value: meansLTex },
      means_u: { value: meansUTex },
      quats: { value: quatsTex },
      scales: { value: scalesTex },
      sh0: { value: sh0Tex },
      sh_labels: { value: shLabelsTex },
      numSplats: { value: sogsData.numSplats },
    }

    // 准备 defines
    const defines: Record<string, string> = {}

    if (meta.version === 2) {
      // V2 格式：使用 codebook
      if (meta.scales.codebook && meta.sh0.codebook) {
        uniforms.scales_codebook = { value: this.createCodebookUniform(meta.scales.codebook) }
        uniforms.sh0_codebook = { value: this.createCodebookUniform(meta.sh0.codebook) }
      }
    } else {
      // V1 格式：使用 mins/maxs
      defines.REORDER_V1 = '1'
      uniforms.scalesMins = { value: new THREE.Vector3(...meta.scales.mins!) }
      uniforms.scalesMaxs = { value: new THREE.Vector3(...meta.scales.maxs!) }
      uniforms.sh0Mins = { value: new THREE.Vector4(...meta.sh0.mins!) }
      uniforms.sh0Maxs = { value: new THREE.Vector4(...meta.sh0.maxs!) }
    }

    // 创建 MRT 渲染目标
    const mrt = this.createMRTTarget(width, height)

    // 创建 shader material
    const material = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: fullscreenVS,
      fragmentShader: this.processedReorderFragment,
      uniforms,
      defines,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending,
    })

    this.fullscreenQuad.material = material

    // 禁用 autoClear：此方法从异步加载链调用，运行在 pipeline.execute() 之外，
    // 不能依赖管线全局设置；MRT 含 RGBA32UI 整数附件，glClear 浮点值不兼容
    const oldRenderTarget = this.renderer.getRenderTarget()
    const oldAutoClear = this.renderer.autoClear
    this.renderer.autoClear = false
    this.renderer.setRenderTarget(mrt)
    this.renderer.render(this.quadScene, this.quadCamera)
    this.renderer.setRenderTarget(oldRenderTarget)
    this.renderer.autoClear = oldAutoClear

    // 获取输出纹理
    const packedTexture = mrt.textures[0]
    const packedSh0 = mrt.textures[1]

    // 清理临时资源
    meansLTex.dispose()
    meansUTex.dispose()
    quatsTex.dispose()
    scalesTex.dispose()
    sh0Tex.dispose()
    if (sogsData.shLabels) shLabelsTex.dispose()
    material.dispose()

    endPack()

    return {
      packedTexture,
      packedSh0,
      width,
      height,
    }
  }

  /**
   * 创建源纹理
   *
   * 关键修复：确保 data 长度与 width * height * 4 匹配
   * Three.js 内部使用 width * height 计算期望大小，如果不匹配会导致 WebGL 错误
   */
  private createSourceTexture(
    data: Uint8Array,
    width: number,
    height: number,
    name: string
  ): THREE.DataTexture {
    // 确保数据大小正确
    const expectedSize = width * height * 4
    let textureData: Uint8Array

    if (data.length === expectedSize) {
      textureData = data
    } else {
      // 创建正确大小的数组并复制数据
      textureData = new Uint8Array(expectedSize)
      textureData.set(data.subarray(0, Math.min(data.length, expectedSize)))
    }

    const texture = new THREE.DataTexture(
      textureData,
      width,
      height,
      THREE.RGBAFormat,
      THREE.UnsignedByteType
    )
    texture.name = name
    texture.minFilter = THREE.NearestFilter
    texture.magFilter = THREE.NearestFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.generateMipmaps = false
    texture.needsUpdate = true
    return texture
  }

  /**
   * 创建 MRT 渲染目标
   *
   * Three.js r150+ 使用 WebGLRenderTarget 的 count 选项
   */
  private createMRTTarget(width: number, height: number): THREE.WebGLRenderTarget {
    // 创建 MRT (2 个颜色附件)
    const mrt = new THREE.WebGLRenderTarget(width, height, {
      count: 2,
      depthBuffer: false,
      stencilBuffer: false,
    })

    // Attachment 0: RGBA32UI (打包数据)
    const packedTex = mrt.textures[0]
    packedTex.name = 'sogsPackedData'
    packedTex.internalFormat = 'RGBA32UI'
    packedTex.format = THREE.RGBAIntegerFormat
    packedTex.type = THREE.UnsignedIntType
    packedTex.minFilter = THREE.NearestFilter
    packedTex.magFilter = THREE.NearestFilter
    packedTex.generateMipmaps = false

    // Attachment 1: RGBA8 (颜色)
    const colorTex = mrt.textures[1]
    colorTex.name = 'sogsPackedSh0'
    colorTex.format = THREE.RGBAFormat
    colorTex.type = THREE.UnsignedByteType
    colorTex.minFilter = THREE.NearestFilter
    colorTex.magFilter = THREE.NearestFilter
    colorTex.generateMipmaps = false

    return mrt
  }

  /**
   * 将 codebook 数组转换为 vec4[64] 格式（用于 GLSL uniform）
   */
  private createCodebookUniform(codebook: (number | null)[]): Float32Array {
    // GLSL uniform vec4[64] = 256 个浮点数
    const result = new Float32Array(256)

    for (let i = 0; i < 256; i++) {
      result[i] = codebook[i] ?? 0
    }

    return result
  }

  /**
   * 清理资源
   */
  dispose(): void {
    this.fullscreenQuad.geometry.dispose()
    this.quadScene.clear()
  }

  /**
   * 从 GPU 纹理执行打包
   *
   * 用于 GSplatSogsData 等已有 GPU 纹理的场景
   *
   * @param params 纹理打包参数
   * @returns 打包后的 GPU 纹理
   */
  packFromTextures(params: TexturePackParams): PackedTexturesGPU {
    const { means_l, means_u, quats, scales, sh0, sh_labels, numSplats, meta } = params

    // 关键修复：确保尺寸是整数（ImageBitmap 可能返回浮点数）
    const meansLowImage = getTextureImage(means_l)
    const width = Math.floor(meansLowImage.width)
    const height = Math.floor(meansLowImage.height)

    // 验证纹理数据大小
    const expectedBytes = width * height * 4
    const textures = [
      { name: 'means_l', tex: means_l },
      { name: 'means_u', tex: means_u },
      { name: 'quats', tex: quats },
      { name: 'scales', tex: scales },
      { name: 'sh0', tex: sh0 },
    ]

    // 如果 sh_labels 存在，也验证它
    if (sh_labels) {
      textures.push({ name: 'sh_labels', tex: sh_labels })
    }
    for (const { name, tex } of textures) {
      const dataLength = getTextureImage(tex).data?.length ?? 0
      if (dataLength > 0 && dataLength < expectedBytes) {
        log.error(
          `[SogsGpuPackerGPU] 纹理 ${name} 数据不足! 期望: ${expectedBytes} 字节, 实际: ${dataLength} 字节, 差额: ${expectedBytes - dataLength} 字节`
        )
      }
    }

    // 准备 uniforms（直接使用 GPU 纹理）
    const uniforms: Record<string, THREE.IUniform> = {
      means_l: { value: means_l },
      means_u: { value: means_u },
      quats: { value: quats },
      scales: { value: scales },
      sh0: { value: sh0 },
      sh_labels: { value: sh_labels ?? means_l }, // 如果没有 SH，使用 dummy 纹理
      numSplats: { value: numSplats },
    }

    // 准备 defines
    const defines: Record<string, string> = {}

    if (meta.version === 2) {
      // V2 格式：使用 codebook
      if (meta.scales.codebook && meta.sh0.codebook) {
        uniforms.scales_codebook = { value: this.createCodebookUniform(meta.scales.codebook) }
        uniforms.sh0_codebook = { value: this.createCodebookUniform(meta.sh0.codebook) }
      }
    } else {
      // V1 格式：使用 mins/maxs
      defines.REORDER_V1 = '1'
      uniforms.scalesMins = { value: new THREE.Vector3(...meta.scales.mins!) }
      uniforms.scalesMaxs = { value: new THREE.Vector3(...meta.scales.maxs!) }
      uniforms.sh0Mins = { value: new THREE.Vector4(...meta.sh0.mins!) }
      uniforms.sh0Maxs = { value: new THREE.Vector4(...meta.sh0.maxs!) }
    }

    // 创建 MRT 渲染目标
    const mrt = this.createMRTTarget(width, height)

    // 创建 shader material
    const material = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: fullscreenVS,
      fragmentShader: this.processedReorderFragment,
      uniforms,
      defines,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending,
    })

    this.fullscreenQuad.material = material

    // 禁用 autoClear：此方法从异步加载链调用，运行在 pipeline.execute() 之外，
    // 不能依赖管线全局设置；MRT 含 RGBA32UI 整数附件，glClear 浮点值不兼容
    const oldRenderTarget = this.renderer.getRenderTarget()
    const oldAutoClear = this.renderer.autoClear
    this.renderer.autoClear = false
    this.renderer.setRenderTarget(mrt)
    this.renderer.render(this.quadScene, this.quadCamera)
    this.renderer.setRenderTarget(oldRenderTarget)
    this.renderer.autoClear = oldAutoClear

    // 获取输出纹理
    const packedTexture = mrt.textures[0]
    const packedSh0 = mrt.textures[1]

    // 清理临时 material
    material.dispose()

    return {
      packedTexture,
      packedSh0,
      width,
      height,
    }
  }
}
